/**
 * "Sharpen the screen" — the optional screening facts that change a Stage 1
 * calculation or deterministic check, and nothing else:
 *
 *   - Build phases (cumulative MW + target date) → per-phase water, staffing
 *     and economic screening cases (`siteScreeningInput`).
 *   - Water source → the water-source curtailment check (feasibility).
 *   - Gas access → the fuel-supply long-lead check (feasibility).
 *   - Grid queue status → used in the site evidence when public queue data is
 *     unavailable.
 *
 * Collapsed by default and open once anything is recorded. Every control
 * accepts "Not known" (the explicit-unknown marker) and none is a design
 * choice: technology, cooling and strategy stay Stage 2 decisions. Writes go
 * through the form's `onChange`, i.e. the host's draft + auto-save path.
 */
import React from 'react';
import type { ProjectIntent, ProjectPhase, WaterSourceKind } from '../../../types/siteRag';
import {
  makeEmptyPhase,
  withPhaseCapacityMW,
  withPhaseInServiceDate,
  effectivePhaseCapacity,
} from '../../../lib/projectIntent';
import {
  clearIntentFieldExplicitlyUnknown,
  isIntentFieldExplicitlyUnknown,
  markIntentFieldExplicitlyUnknown,
} from '../../../lib/intent/explicitUnknown';
import { hasDeclaredWaterSource, waterSourceKinds } from '../../../lib/intent/waterSource';

const UNKNOWN = '__unknown__';
const SCREENING_PHASE_LIMIT = 3;

const WATER_OPTIONS: ReadonlyArray<{ value: WaterSourceKind; label: string }> = [
  { value: 'municipal', label: 'Municipal' },
  { value: 'groundwater', label: 'Groundwater' },
  { value: 'surface', label: 'Surface water' },
  { value: 'reuse', label: 'Reuse / reclaimed' },
];

const inputClass = 'h-8 w-full rounded-md border border-border bg-surface px-2 text-sm text-text-primary';

function sortedPhases(intent: ProjectIntent): ProjectPhase[] {
  return [...(intent.phases ?? [])].sort((a, b) => a.sequence - b.sequence);
}

function resequence(phases: ProjectPhase[]): ProjectPhase[] {
  return phases.map((phase, index) => ({
    ...phase,
    sequence: index + 1,
    label: phase.label === `Phase ${phase.sequence}` ? `Phase ${index + 1}` : phase.label,
  }));
}

const ELECTRICITY_SUPPLY_OPTIONS: ReadonlyArray<{
  value: Exclude<NonNullable<ProjectIntent['electricitySupply']>['arrangement'], 'unknown'>; label: string;
}> = [
  { value: 'own-use', label: 'Own use (the operator generates for itself)' },
  { value: 'third-party-onsite-sale', label: 'A separate company sells on-site power to the operator' },
  { value: 'utility-supplied', label: 'The utility supplies the load' },
];

/**
 * Electricity supply / sale arrangement for the regulatory screen. Optional;
 * "Unknown" records the explicit-unknown marker and leaves the field absent.
 * Never derived from the sponsor type or the PPA structure.
 */
export const ElectricitySupplyField: React.FC<{
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testId: string;
  unknownValue: string;
  selectClassName?: string;
}> = ({ intent, onChange, testId, unknownValue, selectClassName = 'w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md' }) => {
  const arrangement = intent.electricitySupply?.arrangement;
  const value = isIntentFieldExplicitlyUnknown(intent, 'electricitySupply') || arrangement === 'unknown' ? unknownValue : (arrangement ?? '');
  return (
    <div className="space-y-1" data-field-id="electricitySupply">
      <div className="type-meta text-text-muted">Electricity supply / sale arrangement</div>
      <select
        value={value}
        onChange={(event) => {
          const raw = event.target.value;
          const chosen = ELECTRICITY_SUPPLY_OPTIONS.find(option => option.value === raw)?.value;
          const sellerName = chosen === 'third-party-onsite-sale' ? intent.electricitySupply?.sellerName : undefined;
          const next: ProjectIntent = {
            ...intent,
            electricitySupply: chosen ? { arrangement: chosen, ...(sellerName ? { sellerName } : {}) } : undefined,
          };
          onChange(raw === unknownValue
            ? markIntentFieldExplicitlyUnknown(next, 'electricitySupply')
            : clearIntentFieldExplicitlyUnknown(next, 'electricitySupply'));
        }}
        className={selectClassName}
        aria-label="Electricity supply or sale arrangement"
        data-testid={testId}
      >
        <option value="">(not set)</option>
        <option value={unknownValue}>Unknown / not yet decided</option>
        {ELECTRICITY_SUPPLY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      {arrangement === 'third-party-onsite-sale' && (
        <input
          type="text"
          placeholder="Selling company (optional)"
          value={intent.electricitySupply?.sellerName ?? ''}
          onChange={(event) => onChange({
            ...intent,
            electricitySupply: { arrangement: 'third-party-onsite-sale', ...(event.target.value.trim() ? { sellerName: event.target.value } : {}) },
          })}
          className={selectClassName}
          aria-label="Selling company"
          data-testid={`${testId}-seller`}
        />
      )}
      <p className="text-[11px] text-text-muted">
        Whether a separate company may sell on-site power to the operator depends on state law and the utility&rsquo;s service territory.
      </p>
    </div>
  );
};

export interface ScreeningSharpenFieldsProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
}

export const ScreeningSharpenFields: React.FC<ScreeningSharpenFieldsProps> = ({ intent, onChange, testIdPrefix }) => {
  const phases = sortedPhases(intent);
  const recorded = phases.length > 0
    || hasDeclaredWaterSource(intent.waterSource)
    || intent.fuelAccess?.supply != null
    || intent.interconnectionStatus?.queueStatus != null
    || intent.electricitySupply?.arrangement != null
    || ['waterSource', 'fuelAccess', 'interconnectionStatus', 'electricitySupply'].some(field => isIntentFieldExplicitlyUnknown(intent, field));

  const setPhases = (next: ProjectPhase[]) => onChange({ ...intent, phases: next.length > 0 ? next : undefined });
  const updatePhase = (id: string, update: (phase: ProjectPhase) => ProjectPhase) =>
    setPhases(phases.map(phase => (phase.id === id ? update(phase) : phase)));

  const selectedWater = waterSourceKinds(intent.waterSource);

  return (
    <details
      className="rounded-lg border border-border-subtle"
      data-testid={`${testIdPrefix}-sharpen`}
      {...(recorded ? { open: true } : {})}
    >
      <summary className="cursor-pointer px-3 py-2 type-meta font-bold text-text-secondary hover:bg-state-hover" data-testid={`${testIdPrefix}-sharpen-toggle`}>
        Sharpen the screen (optional)
      </summary>
      <div className="space-y-4 border-t border-border-subtle px-3 pb-3 pt-2">
        <p className="text-[11px] text-text-muted">
          Known facts that change specific screening calculations. Leave anything you do not know; &ldquo;Not known&rdquo; is a valid answer.
        </p>

        <section className="space-y-2" data-field-id="phases" aria-label="Build phases">
          <div>
            <div className="type-meta font-semibold text-text-primary">Build phases</div>
            <p className="text-[11px] text-text-muted">Cumulative load at each phase&rsquo;s completion and its target date. Adds per-phase water, staffing and economic cases to the screening.</p>
          </div>
          {phases.map(phase => {
            const capacity = effectivePhaseCapacity(phase);
            const rangeOnly = phase.capacityMW == null && capacity.range != null;
            return (
              <div key={phase.id} className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-2" data-testid={`${testIdPrefix}-sharpen-phase-${phase.sequence}`}>
                <span className="text-xs text-text-secondary">{phase.label || `Phase ${phase.sequence}`}</span>
                {rangeOnly ? (
                  <span className="text-xs text-text-muted">{capacity.displayLabel} (edit in Design &amp; report assumptions)</span>
                ) : (
                  <input
                    type="number"
                    min={0}
                    inputMode="decimal"
                    placeholder="Cumulative MW"
                    aria-label={`${phase.label || `Phase ${phase.sequence}`} cumulative MW`}
                    value={phase.capacityMW ?? ''}
                    onChange={(event) => {
                      const raw = event.target.value.trim();
                      const mw = raw === '' ? null : Number(raw);
                      updatePhase(phase.id, current => withPhaseCapacityMW(current, mw != null && Number.isFinite(mw) && mw > 0 ? mw : null));
                    }}
                    className={inputClass}
                    data-testid={`${testIdPrefix}-sharpen-phase-${phase.sequence}-mw`}
                  />
                )}
                <input
                  type="date"
                  aria-label={`${phase.label || `Phase ${phase.sequence}`} target date`}
                  value={phase.targetInServiceDate ?? ''}
                  onChange={(event) => {
                    const date = event.target.value;
                    updatePhase(phase.id, current => (date
                      ? withPhaseInServiceDate(current, date)
                      : { ...current, targetInServiceDate: null, codTimeline: 'undecided' }));
                  }}
                  className={inputClass}
                  data-testid={`${testIdPrefix}-sharpen-phase-${phase.sequence}-date`}
                />
                <button
                  type="button"
                  onClick={() => setPhases(resequence(phases.filter(entry => entry.id !== phase.id)))}
                  className="text-[11px] text-text-muted hover:text-danger"
                  aria-label={`Remove ${phase.label || `Phase ${phase.sequence}`}`}
                  data-testid={`${testIdPrefix}-sharpen-phase-${phase.sequence}-remove`}
                >
                  Remove
                </button>
              </div>
            );
          })}
          {phases.length < SCREENING_PHASE_LIMIT ? (
            <button
              type="button"
              onClick={() => setPhases([...phases, makeEmptyPhase(phases.length + 1)])}
              className="text-xs font-semibold text-primary hover:underline"
              data-testid={`${testIdPrefix}-sharpen-add-phase`}
            >
              + Add a build phase
            </button>
          ) : (
            <p className="text-[11px] text-text-muted">Add further phases, ranges and per-phase technology in Design &amp; report assumptions.</p>
          )}
        </section>

        <section className="space-y-1" data-field-id="waterSource" aria-label="Water source">
          <div className="type-meta font-semibold text-text-primary">Water source</div>
          <p className="text-[11px] text-text-muted">Feeds the screening&rsquo;s water-source curtailment check.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1" role="group" aria-label="Water source" data-testid={`${testIdPrefix}-sharpen-water`}>
            {WATER_OPTIONS.map(({ value, label }) => {
              const checked = selectedWater.includes(value);
              return (
                <label key={value} className="flex items-center gap-1.5 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? selectedWater.filter(kind => kind !== value) : [...selectedWater, value];
                      const { source: _legacy, ...rest } = intent.waterSource ?? {};
                      void _legacy;
                      onChange(clearIntentFieldExplicitlyUnknown({
                        ...intent,
                        waterSource: next.length === 0 && rest.rightsStatus == null ? undefined : { ...rest, sources: next },
                      }, 'waterSource'));
                    }}
                    className="h-4 w-4 accent-accent"
                    data-testid={`${testIdPrefix}-sharpen-water-${value}`}
                  />
                  {label}
                </label>
              );
            })}
            <label className="flex items-center gap-1.5 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={isIntentFieldExplicitlyUnknown(intent, 'waterSource')}
                onChange={(event) => onChange(event.target.checked
                  ? markIntentFieldExplicitlyUnknown({ ...intent, waterSource: undefined }, 'waterSource')
                  : clearIntentFieldExplicitlyUnknown(intent, 'waterSource'))}
                className="h-4 w-4 accent-accent"
                data-testid={`${testIdPrefix}-sharpen-water-unknown`}
              />
              Not known
            </label>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <section className="space-y-1" data-field-id="fuelAccess" aria-label="Gas access">
            <div className="type-meta font-semibold text-text-primary">Gas access</div>
            <p className="text-[11px] text-text-muted">Feeds the fuel-supply lead-time check for on-site generation.</p>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'fuelAccess') ? UNKNOWN : (intent.fuelAccess?.supply ?? '')}
              onChange={(event) => {
                const raw = event.target.value;
                const next: ProjectIntent = {
                  ...intent,
                  fuelAccess: !raw || raw === UNKNOWN
                    ? undefined
                    : { ...intent.fuelAccess, supply: raw as NonNullable<ProjectIntent['fuelAccess']>['supply'] },
                };
                onChange(raw === UNKNOWN ? markIntentFieldExplicitlyUnknown(next, 'fuelAccess') : clearIntentFieldExplicitlyUnknown(next, 'fuelAccess'));
              }}
              className={inputClass}
              data-testid={`${testIdPrefix}-sharpen-fuel`}
            >
              <option value="">(not set)</option>
              <option value={UNKNOWN}>Not known</option>
              <option value="none">No gas access</option>
              <option value="near-pipeline">Near a pipeline</option>
              <option value="lateral-required">Lateral required</option>
              <option value="firm-contracted">Firm transport contracted</option>
            </select>
          </section>

          <section className="space-y-1" data-field-id="interconnectionStatus" aria-label="Utility service / interconnection status">
            <div className="type-meta font-semibold text-text-primary">Utility service / interconnection status</div>
            <p className="text-[11px] text-text-muted">Record the applicable utility milestone. An accepted load-service agreement does not complete delivery or approve a separate generator request.</p>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'interconnectionStatus') ? UNKNOWN : (intent.interconnectionStatus?.queueStatus ?? '')}
              onChange={(event) => {
                const raw = event.target.value;
                const next: ProjectIntent = {
                  ...intent,
                  interconnectionStatus: !raw || raw === UNKNOWN
                    ? undefined
                    : { ...intent.interconnectionStatus, queueStatus: raw as NonNullable<ProjectIntent['interconnectionStatus']>['queueStatus'] },
                };
                onChange(raw === UNKNOWN
                  ? markIntentFieldExplicitlyUnknown(next, 'interconnectionStatus')
                  : clearIntentFieldExplicitlyUnknown(next, 'interconnectionStatus'));
              }}
              className={inputClass}
              data-testid={`${testIdPrefix}-sharpen-grid`}
            >
              <option value="">(not set)</option>
              <option value={UNKNOWN}>Not known</option>
              <option value="none">No request yet</option>
              <option value="pre-application">Pre-application / scoping</option>
              <option value="queued">In queue</option>
              <option value="study">Study underway</option>
              <option value="executed-LGIA">Executed generator LGIA</option>
              <option value="executed-load-service">Load-service agreement accepted</option>
            </select>
          </section>
        </div>

        <section className="space-y-1" aria-label="Electricity supply" data-testid={`${testIdPrefix}-sharpen-regulatory`}>
          <p className="text-[11px] text-text-muted">
            Feeds the regulatory screen. Behind-the-meter generation can run islanded or grid-parallel; record each phase&rsquo;s grid export and generation duty in Design &amp; report assumptions.
          </p>
          <ElectricitySupplyField
            intent={intent}
            onChange={onChange}
            testId={`${testIdPrefix}-sharpen-electricity-supply`}
            unknownValue={UNKNOWN}
            selectClassName={inputClass}
          />
        </section>
      </div>
    </details>
  );
};
